/**
 * Phase 21β-3.2 — Bausteine/Snippet-Picker für Editoren.
 *
 * Zeigt drei Quellen an, aus denen der Nutzer Inhalte in den aktuellen
 * Editor einfügen kann:
 *   1. **Eigene Snippets** (settings.snippets) — kurze Textbausteine mit Tags
 *   2. **Andere Vorlagen** (templates) — fügt deren Body als Block ein
 *   3. **Vertragsbausteine** (contract_templates → blocks) — einzelne §-Blöcke
 *
 * Aufruf:
 *   <SnippetPicker open={open} onOpenChange={setOpen} onInsert={(html) => …} />
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import {
  MagnifyingGlass, FileText, Briefcase, Stack, Plus,
} from "@phosphor-icons/react";


export default function SnippetPicker({
  open, onOpenChange, onInsert, currentTemplateId,
}) {
  const [tab, setTab] = useState("snippets"); // snippets | templates | contract_blocks
  const [search, setSearch] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contractBlocks, setContractBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/snippets").then((r) => r.data || []).catch(() => []),
      api.get("/templates").then((r) => (r.data || []).filter((x) => !x.system_key)).catch(() => []),
      api.get("/contract-templates").then((r) => r.data || []).catch(() => []),
    ]).then(async ([sn, tpls, ctpls]) => {
      setSnippets(sn);
      setTemplates(tpls.filter((t) => t.id !== currentTemplateId && !t.is_archived));
      // Lade die vollen Blöcke aller Vertragsvorlagen
      const allBlocks = [];
      for (const ct of ctpls.filter((c) => !c.is_archived)) {
        try {
          const { data } = await api.get(`/contract-templates/${ct.id}`);
          for (const b of (data.blocks || [])) {
            allBlocks.push({
              ...b,
              _template_id: ct.id,
              _template_name: ct.name,
              _contract_type: ct.contract_type,
            });
          }
        } catch { /* skip */ }
      }
      setContractBlocks(allBlocks);
    }).finally(() => setLoading(false));
  }, [open, currentTemplateId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return tab === "snippets" ? snippets
           : tab === "templates" ? templates
           : contractBlocks;
    }
    if (tab === "snippets") {
      return snippets.filter((s) =>
        (s.name + " " + (s.category || "") + " " + (s.text || s.body || ""))
          .toLowerCase().includes(q));
    }
    if (tab === "templates") {
      return templates.filter((t) =>
        (t.name + " " + (t.category || "") + " " + (t.subject || ""))
          .toLowerCase().includes(q));
    }
    return contractBlocks.filter((b) =>
      ((b.title || "") + " " + (b._template_name || "") + " " + (b.category || "") + " " + (b.body || ""))
        .toLowerCase().includes(q));
  }, [search, tab, snippets, templates, contractBlocks]);

  const insert = (html, label, tpl) => {
    if (!html || !html.trim()) {
      toast.error("Baustein ist leer");
      return;
    }
    onInsert(html, tpl);
    toast.success(`„${label}" eingefügt`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="snippet-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stack size={18} className="text-emerald-600" />
            Baustein einfügen
          </DialogTitle>
          <DialogDescription>
            Snippets, Vorlagen-Bodies oder einzelne Vertragsbausteine in den Text einfügen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 border-b border-slate-200 -mx-6 px-6">
          {[
            { key: "snippets", label: "Snippets", icon: Stack, count: snippets.length },
            { key: "templates", label: "Andere Vorlagen", icon: FileText, count: templates.length },
            { key: "contract_blocks", label: "Vertragsbausteine", icon: Briefcase, count: contractBlocks.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-emerald-500 text-emerald-700"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`snip-tab-${t.key}`}
            >
              <t.icon size={12} />
              {t.label}
              <span className="text-[10px] text-slate-500 tabular-nums">({t.count})</span>
            </button>
          ))}
        </div>

        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Durchsuchen …"
            className="pl-8 h-8 bg-slate-50 text-xs"
            autoFocus
            data-testid="snip-search"
          />
        </div>

        <div className="max-h-[55vh] overflow-y-auto space-y-1 pr-1" data-testid="snip-results">
          {loading && <div className="text-xs text-slate-500 py-4 text-center">Lade …</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-xs text-slate-500 py-6 text-center">Keine Treffer</div>
          )}
          {!loading && tab === "snippets" && filtered.map((s) => (
            <SnippetItem key={s.id} title={s.name}
                         meta={s.category || ""}
                         body={s.text || s.body}
                         onInsert={() => insert(s.text || s.body, s.name)} />
          ))}
          {!loading && tab === "templates" && filtered.map((t) => (
            <SnippetItem key={t.id} title={t.name}
                         meta={[t.category, t.subject].filter(Boolean).join(" · ")}
                         body={t.body}
                         onInsert={() => insert(t.body, t.name, t)} />
          ))}
          {!loading && tab === "contract_blocks" && filtered.map((b) => (
            <SnippetItem key={b.id + b._template_id} title={b.title || "Ohne Titel"}
                         meta={`${b._template_name}${b.category ? " · " + b.category : ""}${b.optional ? " · optional" : ""}`}
                         body={b.body}
                         onInsert={() => insert(b.body, b.title || "Baustein")} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SnippetItem({ title, meta, body, onInsert }) {
  const preview = (body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200);
  return (
    <div className="border border-slate-200 rounded-sm p-2 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{title}</div>
          {meta && <div className="text-[10px] text-slate-500 truncate">{meta}</div>}
          {preview && (
            <div className="text-[11px] text-slate-600 line-clamp-2 mt-1">{preview}</div>
          )}
        </div>
        <Button size="sm" onClick={onInsert}
                className="bg-slate-900 hover:bg-slate-800 gap-1 h-7 shrink-0 opacity-70 group-hover:opacity-100">
          <Plus size={12} /> Einfügen
        </Button>
      </div>
    </div>
  );
}
